import { useEffect, useRef, useState } from 'react';
import {
  Sparkles,
  Send,
  Loader2,
  Trash2,
  BookOpen,
  Layers,
  Baby,
  FlaskConical,
  CircleHelp,
  ChevronDown,
  ChevronRight,
  X,
  Volume2,
  BookmarkCheck,
} from 'lucide-react';
import Markdown from './Markdown';
import RichExplanation from './RichExplanation';
import { askTutor, type TutorMode, type TutorQuestion } from '../services/tutorApi';
import { useReadAloud } from '../hooks/useReadAloud';
import PanelWidthToggle from './PanelWidthToggle';
import {
  loadConversation, saveConversation, clearConversation,
} from '../store/tutorConversations';
import { useAuthStore } from '../../../stores/authStore';

interface TutorPanelProps {
  lessonId: string;
  slideIndex: number;
  // Display label for the current slide ("grounded in" note + quiz heading).
  slideTitle?: string;
  // Optional: collapse the panel (rendered as a header close button when given).
  onClose?: () => void;
  /** Panel width, owned by LecturePlayer — the toggle lives in this header
   *  beside Clear and Close rather than in a strip of its own. */
  wide?: boolean;
  onToggleWide?: () => void;
}

// A turn in the tutor conversation. Assistant turns may carry either prose
// markdown OR a structured mini-quiz (questions), plus optional citations.
interface Turn {
  id: string;
  role: 'user' | 'assistant';
  // For user turns: the typed/triggered text. For assistant prose turns: markdown.
  content: string;
  // Assistant-only: rich cached HTML (BP 11), present for a mode chip pressed
  // with no typed message. UNTRUSTED model output — rendered only through
  // <RichExplanation>, which sanitises it with DOMPurify first.
  html?: string;
  // Assistant-only.
  questions?: TutorQuestion[];
  citations?: string[];
  mode?: TutorMode;
  error?: boolean;
  // Assistant-only. Whether this answer came from the shared explanation cache
  // or was generated (and billed) just now. Undefined on a live chat turn,
  // which is never cacheable and never claims to be either way.
  cached?: boolean;
}

// Quick-action chips — each maps to a tutor mode. Order matters: it's the
// student's likely escalation path (explain → deeper / simpler → example → check).
const CHIPS: Array<{ mode: TutorMode; label: string; icon: typeof BookOpen }> = [
  { mode: 'explain', label: 'Explain', icon: BookOpen },
  { mode: 'deeper', label: 'Go deeper', icon: Layers },
  { mode: 'eli5', label: 'Simpler', icon: Baby },
  { mode: 'example', label: 'Worked example', icon: FlaskConical },
  { mode: 'check', label: 'Check my understanding', icon: CircleHelp },
];

const PROMPT_LABEL: Record<TutorMode, string> = {
  explain: 'Explain this slide',
  deeper: 'Go deeper on this slide',
  eli5: 'Explain this slide more simply',
  example: 'Show a worked example',
  quiz: 'Quiz me on this slide',
  check: 'Check my understanding',
  chat: '',
};

/**
 * Should the separate "why" be shown under the answer?
 *
 * Two sources feed these questions and they differ. A LIVE model, per the
 * schema's own note, tends to fold its rationale into `a` — so rendering
 * `explanation` as well would say the same thing twice. An AUTHORED set keeps
 * them apart: the author writes a complete answer and one separate sentence of
 * reasoning.
 *
 * Until now nothing rendered `explanation` at all, so roughly seven hundred
 * authored sentences of reasoning existed for a student who could never see
 * them — they reached the marking prompt and stopped there. An answer followed
 * by why it is the answer is the entire point of a check.
 *
 * So: return it when there is one and the answer does not already contain it.
 * The containment test is deliberately loose (case and whitespace folded)
 * because the duplicate it is guarding against is a near-copy, not a byte
 * match, and showing the same sentence twice is worse than omitting a
 * borderline one.
 */
function whyToShow(qa: TutorQuestion): string | null {
  const why = qa.explanation?.trim();
  if (!why) return null;
  const fold = (t: string) => t.toLowerCase().replace(/\s+/g, ' ');
  // Returns the STRING rather than a boolean so the caller keeps the narrowing
  // — TypeScript cannot see through a predicate into an optional field, and
  // `explanation!` would be asserting exactly what this function just checked.
  return fold(qa.a).includes(fold(why)) ? null : why;
}

let turnSeq = 0;
const nextId = () => `t${Date.now()}_${turnSeq++}`;

// Intelligent, context-aware AI tutor for the lecture player. Self-contained:
// holds its own conversation state, talks to /ai/tutor via tutorApi, and renders
// with ws- tokens (light/dark). Mount it in the right rail; pass the active
// lessonId + slideIndex so every turn is grounded in the slide on screen.
export default function TutorPanel({ lessonId, slideIndex, slideTitle, onClose, wide, onToggleWide }: TutorPanelProps) {
  // Seeded from the store, so closing the panel and opening it again shows the
  // conversation rather than an empty tutor. Keyed per slide — see the store.
  const [turns, setTurns] = useState<Turn[]>(() => loadConversation<Turn>(lessonId, slideIndex));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // Which chip is running, so the spinner can sit ON it rather than leaving all
  // five greyed out with no clue which one was pressed.
  const [runningMode, setRunningMode] = useState<TutorMode | null>(null);
  // A fresh generation takes ten to twenty seconds. A bare "Thinking…" that
  // long reads as broken, so it escalates rather than sitting there.
  const [waitedLong, setWaitedLong] = useState(false);
  // Which mini-quiz answers are currently revealed, keyed by `${turnId}:${i}`.
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  // Read aloud, moved here when the Explain panel was folded into this one.
  // Admin-only, and hidden rather than disabled for students — a control a
  // student can see but never use is worse than no control. Instructor: "the
  // read aloud button will burn lots of tokens and we cant afford to keep
  // pulling so only for admin it show be presented."
  const isAdmin = useAuthStore((st) => !!(st.user?.is_admin || st.user?.is_super_admin));
  const { read, state: ttsState } = useReadAloud();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Persist every change, so an unmount (the student pressed Close) loses
  // nothing. Writing on change rather than on unmount is deliberate: an unmount
  // cleanup that closes over stale state is the classic way to save the
  // conversation as it was two turns ago.
  useEffect(() => {
    saveConversation(lessonId, slideIndex, turns);
  }, [lessonId, slideIndex, turns]);

  // The panel stays mounted while the student moves between slides, so the
  // conversation has to follow the slide rather than the mount.
  // The last thing the tutor said was a set of questions, so the composer is
  // for answering them rather than for asking something new.
  const lastTurn = turns[turns.length - 1];
  const awaitingAnswer = !busy
    && lastTurn?.role === 'assistant'
    && !!lastTurn.questions?.length;

  const slideKey = `${lessonId}:${slideIndex}`;
  const lastSlideKey = useRef(slideKey);
  useEffect(() => {
    if (lastSlideKey.current === slideKey) return;
    lastSlideKey.current = slideKey;
    setTurns(loadConversation<Turn>(lessonId, slideIndex));
    setRevealed({});
  }, [slideKey, lessonId, slideIndex]);

  // Eight seconds: long enough that a cached answer never trips it, short
  // enough that nobody has yet decided the thing is broken.
  useEffect(() => {
    if (!busy) return undefined;
    const t = setTimeout(() => setWaitedLong(true), 8000);
    return () => clearTimeout(t);
  }, [busy]);

  // Keep the conversation pinned to the latest turn.
  useEffect(() => {
    // Element.scrollTo is not universal — jsdom has no implementation at all,
    // and it is absent in some embedded webviews. Pinning to the latest turn is
    // a nicety; throwing out of an effect over it takes the whole panel down.
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight });
    } else if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns, busy]);

  const run = async (mode: TutorMode, message?: string) => {
    if (busy) return;
    const userLabel = mode === 'chat' ? (message ?? '') : PROMPT_LABEL[mode];
    if (mode === 'chat' && !userLabel.trim()) return;

    const userTurn: Turn = { id: nextId(), role: 'user', content: userLabel };
    setTurns((prev) => [...prev, userTurn]);
    setBusy(true);
    setRunningMode(mode);
    setWaitedLong(false);
    try {
      const res = await askTutor({ lessonId, slideIndex, mode, message });
      setTurns((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content: res.text,
          html: res.html,
          questions: res.questions,
          citations: res.citations,
          mode: res.mode,
          cached: res.cached,
        },
      ]);
    } catch {
      setTurns((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content: 'Sorry — the tutor could not respond just now. Please try again.',
          error: true,
        },
      ]);
    } finally {
      setBusy(false);
      setRunningMode(null);
      setWaitedLong(false);
    }
  };

  const submitChat = () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput('');
    run('chat', msg);
  };

  const clear = () => {
    setTurns([]);
    setRevealed({});
    clearConversation(lessonId, slideIndex);
  };

  return (
    <div className="flex h-full flex-col bg-ws-surface">
      <header className="flex items-center justify-between border-b border-ws-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-ws-ai" />
          <h2 className="text-sm font-semibold text-ws-ink">AI Tutor</h2>
        </div>
        <div className="flex items-center gap-1">
          {onToggleWide && <PanelWidthToggle wide={!!wide} onToggle={onToggleWide} />}
          {turns.length > 0 && (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              className="grid size-9 sm:size-7 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent disabled:opacity-40"
              title="Clear conversation"
              aria-label="Clear conversation"
            >
              <Trash2 size={14} />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="grid size-9 sm:size-7 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
              title="Close tutor"
              aria-label="Close tutor"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </header>

      {/* Quick-action chips */}
      <div className="flex flex-wrap gap-1.5 border-b border-ws-border px-3 py-2.5">
        {CHIPS.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            onClick={() => run(mode)}
            disabled={busy}
            // The RUNNING chip keeps full contrast and an accent border while
            // the others fade: five identically greyed chips tell a student
            // that something is disabled, not that their press was heard.
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent disabled:cursor-not-allowed ${
              runningMode === mode
                ? 'border-ws-ai bg-ws-ai-quiet text-ws-ai-deep disabled:opacity-100'
                : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:border-ws-ai hover:bg-ws-ai-quiet hover:text-ws-ai-deep disabled:opacity-40'
            }`}
            title={label}
            aria-busy={runningMode === mode}
          >
            {runningMode === mode
              ? <Loader2 size={12} className="animate-spin" />
              : <Icon size={12} />}
            {label}
          </button>
        ))}
      </div>

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
        role="log"
        aria-live="polite"
        aria-label="Tutor conversation"
      >
        {turns.length === 0 && !busy && (
          <div className="px-1 text-xs leading-relaxed text-ws-ink-3">
            <p>
              Your tutor for{' '}
              <span className="text-ws-ink-2">
                {slideTitle ? `"${slideTitle}"` : `slide ${slideIndex + 1}`}
              </span>
              . Tap an action above, or ask anything below — answers are grounded
              in this lecture.
            </p>
          </div>
        )}

        {turns.map((turn) =>
          turn.role === 'user' ? (
            <div key={turn.id} className="flex justify-end">
              <div className="max-w-[85%] break-words [overflow-wrap:anywhere] rounded-lg rounded-br-sm bg-ws-accent px-3 py-2 text-sm text-white">
                {turn.content}
              </div>
            </div>
          ) : (
            <div key={turn.id} className="flex flex-col gap-1.5">
              <div
                {...(turn.error ? { role: 'alert' as const } : {})}
                className={`max-w-[94%] break-words [overflow-wrap:anywhere] rounded-lg rounded-bl-sm px-3 py-2.5 ${
                  turn.error ? 'bg-ws-surface-2 text-ws-danger' : 'bg-ws-surface-2'
                }`}
              >
                {/* Mini-quiz */}
                {turn.questions && turn.questions.length > 0 ? (
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ws-ai-deep">
                      <CircleHelp size={13} />
                      Check your understanding
                    </div>
                    {/* The half of this feature that was invisible.
                        "Check my understanding" is two steps: it asks, you
                        answer, it marks the answer against the slide. The panel
                        only ever showed step one — two questions and a Reveal
                        answer link — so it read as a static quiz, and Reveal
                        invites a student to skip straight past the checking to
                        the answer, which is the opposite of the point. */}
                    <p className="text-xs leading-relaxed text-ws-ink-2">
                      Answer in your own words in the box below and the tutor will mark it against
                      this slide — what you got right, what is missing, and what to do next.
                      <span className="text-ws-ink-3"> Reveal answer is there for afterwards.</span>
                    </p>
                    {turn.questions.map((qa, i) => {
                      const key = `${turn.id}:${i}`;
                      const open = !!revealed[key];
                      const why = whyToShow(qa);
                      return (
                        <div
                          key={key}
                          className="rounded-md border border-ws-border bg-ws-surface px-3 py-2"
                        >
                          <div className="text-sm font-medium text-ws-ink">
                            <span className="mr-1 text-ws-ink-4">{i + 1}.</span>
                            <Markdown compact className="inline-block">
                              {qa.q}
                            </Markdown>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setRevealed((r) => ({ ...r, [key]: !r[key] }))
                            }
                            aria-expanded={open}
                            className="mt-1.5 inline-flex items-center gap-1 rounded text-[11px] font-medium text-ws-ai-deep transition-colors hover:text-ws-ai focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
                          >
                            {open ? (
                              <ChevronDown size={12} />
                            ) : (
                              <ChevronRight size={12} />
                            )}
                            {open ? 'Hide answer' : 'Reveal answer'}
                          </button>
                          {open && (
                            <div className="mt-1.5 border-t border-ws-border pt-1.5">
                              <Markdown compact>{qa.a}</Markdown>
                              {/* The WHY, when it is not already in the answer. See whyToShow. */}
                              {why ? (
                                <p className="mt-1.5 text-[12px] leading-relaxed text-ws-ink-3">
                                  <Markdown compact className="inline">{why}</Markdown>
                                </p>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : turn.error ? (
                  <p className="text-sm">{turn.content}</p>
                ) : turn.html ? (
                  /* A cached explanation: rich HTML written once by a strong
                     model, sanitised inside RichExplanation before it reaches
                     the DOM. Falls back to markdown for every live turn. */
                  <RichExplanation html={turn.html} />
                ) : (
                  <Markdown compact>{turn.content}</Markdown>
                )}

                {/* Where this answer came from, and what it cost.
                    "Saved explanation" is not a performance boast — it tells
                    the student this is the course's settled answer for the
                    slide, the same one everyone else sees, and that pressing
                    the chip again will not read differently. "Generated just
                    now" is the honest complement: before this, a freshly
                    generated answer was silent, so the one case that spends
                    money on every press was the one case with no tell. A
                    typed question is neither — it is always live and never
                    claims otherwise. */}
                {turn.role === 'assistant' && !turn.error && turn.cached !== undefined && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-ws-border pt-2">
                    <span className="inline-flex items-center gap-1.5 text-[10.5px] text-ws-ink-4">
                      {turn.cached ? <BookmarkCheck size={12} /> : <Sparkles size={12} className="text-ws-ai" />}
                      {turn.cached ? 'Saved explanation' : 'Generated just now'}
                    </span>
                    {isAdmin && turn.content && (
                      <button
                        type="button"
                        onClick={() => read(turn.content)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-ws-border bg-ws-surface px-2 py-1 text-[10.5px] font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
                      >
                        {ttsState === 'loading'
                          ? <Loader2 size={12} className="animate-spin" />
                          : <Volume2 size={12} />}
                        {ttsState === 'idle' ? 'Read aloud' : 'Stop'}
                      </button>
                    )}
                  </div>
                )}

                {/* Citations */}
                {turn.citations && turn.citations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 border-t border-ws-border pt-2">
                    {turn.citations.map((c, i) => (
                      <span
                        key={c}
                        title={c}
                        className="rounded bg-ws-ai-pale px-1.5 py-0.5 text-[10px] font-medium text-ws-ai-deep"
                      >
                        [{i + 1}]
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {busy && (
          // Names the thing being fetched rather than saying "Thinking…" into
          // the void, and escalates once the wait is long enough to look like a
          // failure. role="status" so a screen reader hears it without the
          // focus moving.
          <div role="status" aria-live="polite" className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-ws-ink-2">
              <Loader2 size={14} className="animate-spin text-ws-ai" />
              {runningMode && runningMode !== 'chat'
                ? `Working on "${CHIPS.find((c) => c.mode === runningMode)?.label ?? runningMode}"…`
                : 'Thinking…'}
            </div>
            {waitedLong && (
              <p className="pl-6 text-xs text-ws-ink-3">
                This one has not been written yet, so it is being written now — usually another
                ten seconds or so. It will be instant next time.
              </p>
            )}
          </div>
        )}
      </div>

      {/* "Grounded in this lecture" note */}
      <div className="flex items-center gap-1.5 border-t border-ws-border px-4 py-1.5 text-[10.5px] text-ws-ink-4">
        <Sparkles size={11} className="text-ws-ai" />
        Grounded in this lecture
      </div>

      {/* Chat input */}
      <footer className="border-t border-ws-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitChat();
              }
            }}
            rows={1}
            // When the tutor has just asked something, the box is where the
            // answer goes — saying "Ask a follow-up" there is why the whole
            // exchange read as one-way.
            placeholder={awaitingAnswer ? 'Type your answer — the tutor will mark it…' : 'Ask a follow-up…'}
            disabled={busy}
            aria-label="Ask the tutor a follow-up question"
            className="max-h-28 min-h-[36px] min-w-0 flex-1 resize-none rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-ink placeholder:text-ws-ink-4 focus:border-ws-accent focus:outline-none disabled:opacity-60"
          />
          <button
            type="button"
            onClick={submitChat}
            disabled={!input.trim() || busy}
            className="grid size-10 sm:size-9 shrink-0 place-items-center rounded-md bg-ws-accent text-white transition-colors hover:bg-ws-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-1 disabled:opacity-40"
            title="Send"
            aria-label="Send message"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </footer>
    </div>
  );
}
